/**
 * Cases Dashboard types — the admin Inbox "Dashboard" tab.
 *
 * GTI-Hub parity with the Backoffice "GTI Project Ops Dashboard" sheet
 * (All Cases / Completed tabs): one row per case (ReportRequest + its
 * 1:1 Report) with a sub-row per Network-Partner assignment. Client /
 * analyst objects reuse the inbox shapes.
 */

import { InboxAnalyst, InboxClient } from '@/types/inbox';

export type DashboardView = 'active' | 'completed';

/** Derived NP-assignment lifecycle for a dashboard sub-row. */
export type DashboardNpStatus =
  | 'draft'
  | 'shortlisting'
  | 'np_locked'
  | 'in_progress'
  | 'submitted';

export interface DashboardNp {
  assignmentId: string;
  partnerName: string | null;
  status: DashboardNpStatus | string;
  /** ISO date (no time) — the partner's internal deadline. */
  npDueDate: string | null;
  submitted: boolean;
  /** Candidates while shortlisting (no partner locked yet). */
  shortlistCount: number;
}

export interface DashboardCase {
  reportRequestId: string;
  reportId: string | null;
  codename: string | null;
  codenameFamily: string | null;
  subjects: string[];
  configName: string | null;
  teamId: string | null;
  client: InboxClient | null;
  /** The client's own reference (matter / PO number). */
  clientReference: string | null;
  analyst: InboxAnalyst | null;
  /** Human QA reviewers hoisted from the report (ops "1st / 2nd QAer")
   * — admin-assigned; null while the case has no report or the slot is
   * unassigned. */
  firstQa: InboxAnalyst | null;
  secondQa: InboxAnalyst | null;
  createdAt: string | null;
  /** Native pipeline stage value (triage stage or report lifecycle). */
  stage: string;
  stageKind: 'triage' | 'report';
  expedited: boolean;
  turnaroundDays: number | null;
  /** The client clock: started_at + effective TAT; null pre-start. */
  dueAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  /** Completed view only: completed on or before the due date. */
  onTime: boolean | null;
  analystNotes: string | null;
  nps: DashboardNp[];
}

export interface CaseDashboardResponse {
  cases: DashboardCase[];
  total: number;
  generatedAt: string;
  /** The view's version token (also the `ETag` header). */
  version: string | null;
}

// ---------------------------------------------------------------------------
// API-shape transformers (snake_case → camelCase)
// ---------------------------------------------------------------------------

interface ApiDashboardNp {
  assignment_id: string;
  partner_name: string | null;
  status: string;
  np_due_date: string | null;
  submitted: boolean;
  shortlist_count: number;
}

interface ApiDashboardCase {
  report_request_id: string;
  report_id: string | null;
  codename: string | null;
  codename_family: string | null;
  subjects: string[];
  config_name: string | null;
  client_reference?: string | null;
  team_id: string | null;
  client: {
    team_id: string;
    name: string;
    client_code: string | null;
    organization_id: number | null;
  } | null;
  analyst: { user_id: string; name?: string | null } | null;
  first_qa: { user_id: string; name?: string | null } | null;
  second_qa: { user_id: string; name?: string | null } | null;
  created_at: string | null;
  stage: string;
  stage_kind: string;
  expedited: boolean;
  turnaround_days: number | null;
  due_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  on_time: boolean | null;
  analyst_notes: string | null;
  nps: ApiDashboardNp[];
}

export interface ApiCaseDashboardResponse {
  cases: ApiDashboardCase[];
  total: number;
  generated_at: string;
  version?: string | null;
}

function transformCase(api: ApiDashboardCase): DashboardCase {
  return {
    reportRequestId: api.report_request_id,
    reportId: api.report_id,
    codename: api.codename,
    codenameFamily: api.codename_family,
    subjects: api.subjects ?? [],
    configName: api.config_name,
    clientReference: api.client_reference ?? null,
    teamId: api.team_id,
    client: api.client
      ? {
          teamId: api.client.team_id,
          name: api.client.name,
          clientCode: api.client.client_code,
          organizationId: api.client.organization_id,
        }
      : null,
    analyst: api.analyst ? { userId: api.analyst.user_id, name: api.analyst.name ?? null } : null,
    firstQa: api.first_qa
      ? { userId: api.first_qa.user_id, name: api.first_qa.name ?? null }
      : null,
    secondQa: api.second_qa
      ? { userId: api.second_qa.user_id, name: api.second_qa.name ?? null }
      : null,
    createdAt: api.created_at,
    stage: api.stage,
    stageKind: api.stage_kind === 'report' ? 'report' : 'triage',
    expedited: api.expedited,
    turnaroundDays: api.turnaround_days,
    dueAt: api.due_at,
    startedAt: api.started_at,
    completedAt: api.completed_at,
    onTime: api.on_time,
    analystNotes: api.analyst_notes,
    nps: (api.nps ?? []).map((np) => ({
      assignmentId: np.assignment_id,
      partnerName: np.partner_name,
      status: np.status,
      npDueDate: np.np_due_date,
      submitted: np.submitted,
      shortlistCount: np.shortlist_count,
    })),
  };
}

export function transformToCaseDashboard(api: ApiCaseDashboardResponse): CaseDashboardResponse {
  return {
    cases: (api.cases ?? []).map(transformCase),
    total: api.total ?? 0,
    generatedAt: api.generated_at,
    version: api.version ?? null,
  };
}
